import { differenceInCalendarDays, format } from 'date-fns';
import { Link } from 'react-router-dom';
import { AlertCircle, BellRing, ChevronRight, CloudOff, FlaskConical, Pill, Plus, Scale } from 'lucide-react';
import { DosesToday, useTodayDoses } from '../meds/DosesToday';
import { useRemindersStatus } from '../reminders/useReminders';
import { Card } from '../../components/Page';
import { useDb } from '../../db/hooks';
import { useQuickLog } from '../../app/QuickLogSheet';
import { useCanWrite } from '../../account/useCanWrite';
import { useOptionalAccount } from '../../account/AccountGate';
import { useSync } from '../../sync/SyncProvider';
import { useSettings } from '../../data/settings';
import { useSessions } from '../../data/sessions';
import { addFluid, useFluidEntries, useWeightEntries } from '../../data/tracking';
import { formatKg } from '../../lib/sessions';
import { gainSoFar } from '../../lib/tracking';
import { useLabReports, useLabResults } from '../../data/labs';
import { isCritical, STATUS_LABELS } from '../../lib/labs';
import { dayRange } from '../../lib/time';

const QUICK_AMOUNTS = [100, 200, 250];

function FluidRing({ total, limit }: { total: number; limit: number | null }) {
  const size = 96;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const share = limit ? Math.min(total / limit, 1) : 0;
  const over = limit != null && total > limit;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgb(var(--rf-line))" strokeWidth={stroke} />
      {limit ? (
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={over ? '#b54708' : 'rgb(var(--rf-series-1))'}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${share * circumference} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      ) : null}
      <text x="50%" y="47%" textAnchor="middle" className="fill-clinical-ink text-base font-semibold">
        {total}
      </text>
      <text x="50%" y="64%" textAnchor="middle" className="fill-clinical-muted text-[10px]">
        mL
      </text>
    </svg>
  );
}

export function FluidTodayCard() {
  const db = useDb();
  const canWrite = useCanWrite();
  const quickLog = useQuickLog();
  const { settings } = useSettings();
  const [from, to] = dayRange();
  const { entries } = useFluidEntries(from, to);
  const total = entries.reduce((sum, entry) => sum + entry.amount_ml, 0);
  const limit = settings.fluid_limit_ml;
  const left = limit != null ? limit - total : null;

  return (
    <Card title="Fluid today">
      <div className="flex items-center gap-4">
        <FluidRing total={total} limit={limit} />
        <div className="min-w-0 flex-1 text-sm">
          {limit == null ? (
            <p className="text-clinical-muted">
              No daily limit set.{' '}
              <Link to="/more/settings" className="font-medium text-clinical-primary">
                Add one
              </Link>
            </p>
          ) : left! >= 0 ? (
            <p>
              <span className="font-semibold">{left} mL</span> <span className="text-clinical-muted">left of {limit} mL</span>
            </p>
          ) : (
            <p className="flex items-center gap-1.5 font-semibold text-clinical-warning">
              <AlertCircle className="h-4 w-4" aria-hidden /> {-left!} mL over your {limit} mL limit
            </p>
          )}
          <p className="text-xs text-clinical-muted">
            {entries.length ? `${entries.length} ${entries.length === 1 ? 'drink' : 'drinks'}, last at ${format(new Date(entries[0].taken_at), 'HH:mm')}` : 'Nothing logged yet today'}
          </p>
        </div>
      </div>
      {canWrite ? (
        <div className="mt-3 grid grid-cols-4 gap-2">
          {QUICK_AMOUNTS.map((amount) => (
            <button
              key={amount}
              type="button"
              onClick={() => void addFluid(db, amount)}
              aria-label={`Add ${amount} mL of water`}
              className="focus-ring min-h-11 rounded-lg border border-clinical-line text-sm font-semibold text-clinical-primary hover:bg-clinical-surface"
            >
              +{amount}
            </button>
          ))}
          <button
            type="button"
            onClick={() => quickLog.open({ kind: 'fluid' })}
            className="focus-ring min-h-11 rounded-lg border border-clinical-line text-sm font-semibold text-clinical-muted hover:bg-clinical-surface"
          >
            Other
          </button>
        </div>
      ) : null}
    </Card>
  );
}

export function WeightTodayCard() {
  const canWrite = useCanWrite();
  const quickLog = useQuickLog();
  const { sessions } = useSessions();
  const { entries } = useWeightEntries(30);
  const sinceSession = gainSoFar(entries, sessions);
  const latest = entries[0];

  return (
    <Card title="Weight">
      <div className="flex items-center gap-3">
        <Scale className="h-6 w-6 shrink-0 text-clinical-primary" />
        <div className="min-w-0 flex-1 text-sm">
          {latest ? (
            <>
              <p>
                <span className="text-lg font-semibold">{formatKg(latest.weight_kg)}</span>{' '}
                <span className="text-clinical-muted">{format(new Date(latest.measured_at), "EEE 'at' HH:mm")}</span>
              </p>
              <p className="text-clinical-muted">
                {sinceSession
                  ? `${formatKg(sinceSession.gain, true)} since your session on ${format(new Date(`${sinceSession.sessionDate}T12:00`), 'EEE d MMM')}`
                  : 'Log a weight after your next session to see the gain.'}
              </p>
            </>
          ) : (
            <p className="text-clinical-muted">No weight logged between sessions yet.</p>
          )}
        </div>
        {canWrite ? (
          <button
            type="button"
            onClick={() => quickLog.open({ kind: 'weight' })}
            className="focus-ring inline-flex min-h-11 items-center gap-1 rounded-lg border border-clinical-line px-3 text-sm font-semibold text-clinical-primary hover:bg-clinical-surface"
          >
            <Plus className="h-4 w-4" /> Weigh
          </button>
        ) : null}
      </div>
    </Card>
  );
}

/** A value past a critical limit in the latest lab report (SPEC.md §5 Today). */
export function LabAlert() {
  const { reports } = useLabReports();
  const { results } = useLabResults();
  const latest = reports[0];
  if (!latest) return null;
  const critical = results.filter((result) => result.report_id === latest.id && isCritical(result.status));
  if (!critical.length) return null;
  return (
    <Link to={`/labs/${latest.id}`} className="focus-ring flex items-center gap-3 rounded-xl border border-red-300/60 bg-red-500/10 p-4 text-sm">
      <FlaskConical className="h-5 w-5 shrink-0 text-red-700 dark:text-red-300" />
      <span className="flex-1">
        <span className="block font-semibold">Lab report {format(new Date(`${latest.result_date}T12:00`), 'd MMM')}</span>
        {critical.map((result) => `${result.test_name_snapshot}: ${STATUS_LABELS[result.status!].toLowerCase()} (${result.raw_value} ${result.unit ?? ''})`.trim()).join(' · ')}
        <span className="block text-xs text-clinical-muted">Compared with the range in your lab settings. Talk to your care team about these results.</span>
      </span>
      <ChevronRight className="h-4 w-4" />
    </Link>
  );
}

/** Medicines due today, tap to mark taken (SPEC.md §5 Today). Hidden until medicines are added. */
export function MedsTodayCard() {
  const { planned, hasMedications } = useTodayDoses();
  if (!hasMedications) return null;
  const taken = planned.filter((dose) => dose.status === 'taken').length;
  return (
    <Card title="Medicines today">
      {planned.length ? <p className="-mt-2 mb-1 text-xs text-clinical-muted">{taken} of {planned.length} taken</p> : null}
      <DosesToday compact />
      <Link to="/health?tab=meds" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-clinical-primary">
        <Pill className="h-4 w-4" /> All medicines
      </Link>
    </Card>
  );
}

const DAY_MS = 86_400_000;

/** Account alerts: changes stuck on this device, and a yearly plan that won't renew (SPEC.md §5 Today). */
export function AccountAlerts() {
  const sync = useSync();
  const account = useOptionalAccount();
  const reminders = useRemindersStatus();
  const alerts: { key: string; icon: typeof AlertCircle; text: string; to: string }[] = [];

  if (reminders === 'stale') {
    alerts.push({ key: 'reminders', icon: BellRing, text: 'Your reminders are out of date. Add them to your calendar again.', to: '/more/reminders' });
  }

  if (sync && sync.status.pending > 0) {
    const last = sync.status.lastSyncedAt ? new Date(sync.status.lastSyncedAt).getTime() : null;
    if (last == null || Date.now() - last > 3 * DAY_MS) {
      alerts.push({
        key: 'sync',
        icon: CloudOff,
        text: `${sync.status.pending} ${sync.status.pending === 1 ? 'change hasn’t' : 'changes haven’t'} reached your account for over 3 days. Connect to the internet to back them up.`,
        to: '/more/account'
      });
    }
  }

  const row = account?.entitlement.row;
  if (row?.plan === 'yearly' && row.current_period_end && (row.status === 'canceled' || row.status === 'past_due')) {
    const days = differenceInCalendarDays(new Date(row.current_period_end), new Date());
    if (days >= 0 && days <= 14) {
      alerts.push({
        key: 'plan',
        icon: AlertCircle,
        text: `Your plan ends ${days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`}. After that you can still view and export your data, but not add to it.`,
        to: '/more/account'
      });
    }
  }

  return (
    <>
      {alerts.map(({ key, icon: Icon, text, to }) => (
        <Link key={key} to={to} className="focus-ring flex items-center gap-3 rounded-xl border border-amber-300/60 bg-amber-500/10 p-4 text-sm">
          <Icon className="h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" />
          <span className="flex-1">{text}</span>
          <ChevronRight className="h-4 w-4" />
        </Link>
      ))}
    </>
  );
}
